import type { IngestionProgress, ProgressEvent } from "./types";

/** Finished successfully. A failed import is never complete, whatever its percent. */
export const isImportComplete = (progress: IngestionProgress) =>
    progress.status !== "failed" && (progress.status === "complete" || progress.percent >= 100);

/** No more frames are coming: succeeded or failed. */
export const isImportFinished = (progress: IngestionProgress) =>
    progress.status === "failed" || isImportComplete(progress);

const finite = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);

/**
 * Folds a `progress` frame into a bar's state.
 *
 * Frames can arrive out of order, so the bar never moves backwards. A frame
 * without a numeric `percent` updates the rest and leaves the percentage alone.
 * Any frame revives a bar that was marked stalled. A final `failed` wins over
 * any percentage.
 */
export function mergeImportProgress(prev: IngestionProgress | undefined, frame: ProgressEvent): IngestionProgress {
    const percent = finite(frame.percent) ? Math.min(100, Math.max(0, frame.percent)) : undefined;
    const failed = frame.status === "failed";
    const complete = !failed && (frame.status === "complete" || frame.status === "succeeded" || percent === 100);
    const processed = [prev?.processed, frame.processed].filter(finite);

    return {
        jobId: prev?.jobId ?? frame.job_id,
        percent: complete ? 100 : Math.max(prev?.percent ?? 0, percent ?? 0),
        processed: processed.length ? Math.max(...processed) : undefined,
        total: finite(frame.total) ? frame.total : prev?.total,
        status: failed
            ? "failed"
            : complete
                ? "complete"
                : (frame.status as IngestionProgress["status"]) ?? (prev?.status === "stalled" ? "importing" : prev?.status),
        // CA-3: keep the first explanation we were given. A later frame that
        // omits `reason` must not erase why the import failed.
        reason: frame.reason ?? prev?.reason,
    };
}
